// Marketing Campaigns Tab
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../ui/dropdown-menu';
import { Loader2, Plus, Edit, Trash2, MoreVertical, Mail, Users, Download, X } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';
import { useConfirm } from '../common/ConfirmDialog';

const MarketingCampaignsTab = ({ tenantId }) => {
  const confirm = useConfirm();
  const [loading, setLoading] = useState(true);
  const [campaigns, setCampaigns] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [campaignDialogOpen, setCampaignDialogOpen] = useState(false);
  const [entitiesDialogOpen, setEntitiesDialogOpen] = useState(false);
  const [addEntityDialogOpen, setAddEntityDialogOpen] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [saving, setSaving] = useState(false);
  
  const [campaignForm, setCampaignForm] = useState({
    name: '',
    campaign_type: 'email',
    description: '',
    status: 'active',
    frequency: 'one_off',
    notes: ''
  });
  
  const [campaignEntities, setCampaignEntities] = useState([]);
  const [entitiesLoading, setEntitiesLoading] = useState(false);
  const [crmEntities, setCrmEntities] = useState([]);
  const [contactEntries, setContactEntries] = useState([]);
  const [editingEntity, setEditingEntity] = useState(null);
  const [entityForm, setEntityForm] = useState({
    crm_entity_id: '',
    contact_entry_id: '',
    notes: ''
  });

  const campaignTypes = [
    { value: 'email', label: 'Email' },
    { value: 'telephone', label: 'Telephone' },
    { value: 'direct_mailer', label: 'Direct Mailer' },
    { value: 'other', label: 'Other' },
  ];

  const frequencyOptions = [
    { value: 'one_off', label: 'One Off' },
    { value: 'daily', label: 'Daily' },
    { value: 'weekly', label: 'Weekly' },
    { value: 'monthly', label: 'Monthly' },
    { value: 'quarterly', label: 'Quarterly' },
    { value: 'annual', label: 'Annual' },
  ];

  useEffect(() => {
    fetchCampaigns();
    fetchCrmEntities();
  }, [tenantId]);

  const fetchCampaigns = async () => {
    try {
      const response = await api.get('/campaigns');
      setCampaigns(response.data || []);
    } catch (error) {
      console.error('Error fetching campaigns:', error);
      toast.error('Failed to load campaigns');
    } finally {
      setLoading(false);
    }
  };

  const fetchCrmEntities = async () => {
    try {
      const response = await api.get('/crm-entities');
      setCrmEntities(response.data || []);
    } catch (error) {
      console.error('Error fetching CRM entities:', error);
    }
  };

  const fetchCampaignEntities = async (campaignId) => {
    setEntitiesLoading(true);
    try {
      const response = await api.get(`/campaigns/${campaignId}/entities`);
      setCampaignEntities(response.data || []);
    } catch (error) {
      console.error('Error fetching campaign entities:', error);
      toast.error('Failed to load campaign entities');
    } finally {
      setEntitiesLoading(false);
    }
  };

  const fetchContactsForEntity = async (entityId) => {
    try {
      const response = await api.get(`/contact-entries?crm_entity_id=${entityId}`);
      setContactEntries(response.data || []);
    } catch (error) {
      console.error('Error fetching contacts:', error);
      setContactEntries([]);
    }
  };

  const handleCreateCampaign = () => {
    setEditingCampaign(null);
    setCampaignForm({
      name: '',
      campaign_type: 'email',
      description: '',
      status: 'active',
      frequency: 'one_off',
      notes: ''
    });
    setCampaignDialogOpen(true);
  };

  const handleEditCampaign = (campaign) => {
    setEditingCampaign(campaign);
    setCampaignForm({
      name: campaign.name,
      campaign_type: campaign.campaign_type || 'email',
      description: campaign.description || '',
      status: campaign.status || (campaign.is_active ? 'active' : 'inactive'),
      frequency: campaign.frequency || 'one_off',
      notes: campaign.notes || ''
    });
    setCampaignDialogOpen(true);
  };

  const handleSaveCampaign = async () => {
    if (!campaignForm.name.trim()) {
      toast.error('Campaign name is required');
      return;
    }

    const payload = {
      ...campaignForm,
      is_active: campaignForm.status === 'active',
    };

    setSaving(true);
    try {
      if (editingCampaign) {
        await api.put(`/campaigns/${editingCampaign.id}`, payload);
        toast.success('Campaign updated');
      } else {
        await api.post('/campaigns', payload);
        toast.success('Campaign created');
      }
      setCampaignDialogOpen(false);
      fetchCampaigns();
    } catch (error) {
      console.error('Error saving campaign:', error);
      toast.error(error.response?.data?.detail || 'Failed to save campaign');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCampaign = async (campaignId) => {
    if (!await confirm({ title: 'Delete Campaign', description: 'Delete this campaign and all its entities? This cannot be undone.', destructive: true })) return;

    try {
      await api.delete(`/campaigns/${campaignId}`);
      toast.success('Campaign deleted');
      fetchCampaigns();
    } catch (error) {
      console.error('Error deleting campaign:', error);
      toast.error('Failed to delete campaign');
    }
  };

  const handleViewEntities = (campaign) => {
    setSelectedCampaign(campaign);
    fetchCampaignEntities(campaign.id);
    setEntitiesDialogOpen(true);
  };

  const handleAddEntity = () => {
    setEditingEntity(null);
    setEntityForm({
      crm_entity_id: '',
      contact_entry_id: '',
      notes: ''
    });
    setContactEntries([]);
    setAddEntityDialogOpen(true);
  };

  const handleEditEntity = async (entity) => {
    setEditingEntity(entity);
    setEntityForm({
      crm_entity_id: entity.crm_entity_id,
      contact_entry_id: entity.contact_entry_id || '',
      notes: entity.notes || ''
    });
    
    // Fetch contacts for this entity
    await fetchContactsForEntity(entity.crm_entity_id);
    setAddEntityDialogOpen(true);
  };

  const handleSaveEntity = async () => {
    if (!entityForm.crm_entity_id) {
      toast.error('Please select an entity');
      return;
    }

    setSaving(true);
    try {
      if (editingEntity) {
        // Update existing entity
        await api.put(`/campaigns/${selectedCampaign.id}/entities/${editingEntity.campaign_entity_id}`, {
          contact_entry_id: entityForm.contact_entry_id || null,
          notes: entityForm.notes
        });
        toast.success('Recipient updated');
      } else {
        // Add new entity
        await api.post(`/campaigns/${selectedCampaign.id}/entities`, entityForm);
        toast.success('Recipient added to campaign');
      }
      
      setAddEntityDialogOpen(false);
      fetchCampaignEntities(selectedCampaign.id);
    } catch (error) {
      console.error('Error saving recipient:', error);
      toast.error(error.response?.data?.detail || 'Failed to save recipient');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveEntity = async (campaignEntityId) => {
    if (!await confirm({ title: 'Remove Entity', description: 'Remove this entity from the campaign?', destructive: true })) return;

    try {
      await api.delete(`/campaigns/${selectedCampaign.id}/entities/${campaignEntityId}`);
      toast.success('Entity removed');
      fetchCampaignEntities(selectedCampaign.id);
    } catch (error) {
      console.error('Error removing entity:', error);
      toast.error('Failed to remove entity');
    }
  };

  const handleExport = async (campaignId) => {
    try {
      const response = await api.get(`/campaigns/${campaignId}/export`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `campaign_export.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Campaign exported');
    } catch (error) {
      console.error('Error exporting:', error);
      toast.error('Failed to export campaign');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Mail className="w-5 h-5" />
            Marketing Campaigns
          </CardTitle>
          <Button onClick={handleCreateCampaign} className="bg-primary hover:bg-primary/90 text-white">
            <Plus className="w-4 h-4 mr-2" />
            Create Campaign
          </Button>
        </CardHeader>
        <CardContent>
          {campaigns.length === 0 ? (
            <p className="text-center py-12 text-muted-foreground">No campaigns created yet</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Campaign Name</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Recipients</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {campaigns.map((campaign) => (
                  <TableRow key={campaign.id}>
                    <TableCell className="font-medium">{campaign.name}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{campaign.campaign_type}</Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant={campaign.status === 'active' ? 'default' : 'secondary'}>
                        {campaign.status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="link"
                        size="sm"
                        onClick={() => handleViewEntities(campaign)}
                        className="p-0 h-auto"
                      >
                        <Users className="w-4 h-4 mr-1" />
                        {campaign.entity_count || 0} recipients
                      </Button>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {new Date(campaign.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleViewEntities(campaign)}>
                            <Users className="w-4 h-4 mr-2" />
                            Manage Recipients
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleEditCampaign(campaign)}>
                            <Edit className="w-4 h-4 mr-2" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleExport(campaign.id)}>
                            <Download className="w-4 h-4 mr-2" />
                            Export to CSV
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            onClick={() => handleDeleteCampaign(campaign.id)}
                            className="text-red-600"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Campaign Dialog */}
      <Dialog open={campaignDialogOpen} onOpenChange={setCampaignDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingCampaign ? 'Edit Campaign' : 'Create Campaign'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Campaign Name *</Label>
              <Input
                value={campaignForm.name}
                onChange={(e) => setCampaignForm({...campaignForm, name: e.target.value})}
                placeholder="e.g., Summer Sale 2026"
              />
            </div>

            <div className="space-y-2">
              <Label>Campaign Type *</Label>
              <Select
                value={campaignForm.campaign_type}
                onValueChange={(value) => setCampaignForm({...campaignForm, campaign_type: value})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {campaignTypes.map((type) => (
                    <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Frequency</Label>
                <Select
                  value={campaignForm.frequency}
                  onValueChange={(value) => setCampaignForm({...campaignForm, frequency: value})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {frequencyOptions.map((f) => (
                      <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Status</Label>
                <Select
                  value={campaignForm.status}
                  onValueChange={(value) => setCampaignForm({...campaignForm, status: value})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="archived">Archived</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={campaignForm.description}
                onChange={(e) => setCampaignForm({...campaignForm, description: e.target.value})}
                placeholder="Campaign details..."
                rows={3}
              />
            </div>

            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={campaignForm.notes}
                onChange={(e) => setCampaignForm({...campaignForm, notes: e.target.value})}
                placeholder="Internal notes..."
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCampaignDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveCampaign} disabled={saving} className="bg-primary hover:bg-primary/90 text-white">
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Campaign Recipients Dialog */}
      <Dialog open={entitiesDialogOpen} onOpenChange={setEntitiesDialogOpen}>
        <DialogContent className="sm:max-w-4xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader className="flex flex-row items-center justify-between">
            <DialogTitle>
              {selectedCampaign?.name} - Recipients
            </DialogTitle>
            <div className="flex gap-2">
              <Button 
                onClick={() => handleExport(selectedCampaign?.id)}
                variant="outline"
                size="sm"
              >
                <Download className="w-4 h-4 mr-2" />
                Export CSV
              </Button>
              <Button 
                onClick={handleAddEntity}
                size="sm"
                className="bg-primary hover:bg-primary/90 text-white"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Recipient
              </Button>
            </div>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto">
            {entitiesLoading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : campaignEntities.length === 0 ? (
              <p className="text-center py-12 text-muted-foreground">
                No recipients added yet. Click &quot;Add Recipient&quot; to get started.
              </p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Contact Detail</TableHead>
                    <TableHead>Notes</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {campaignEntities.map((entity) => {
                    // Format contact detail display
                    let contactDisplay = 'Use any available';
                    if (entity.contact_type && entity.contact_detail) {
                      if (entity.contact_type === 'email') {
                        contactDisplay = `${entity.contact_detail} (Email${entity.contact_subtype ? ' - ' + entity.contact_subtype : ''})`;
                      } else if (entity.contact_type === 'phone') {
                        contactDisplay = `${entity.contact_detail} (Phone${entity.contact_subtype ? ' - ' + entity.contact_subtype : ''})`;
                      } else if (entity.contact_type === 'address') {
                        contactDisplay = `${entity.contact_detail} (Address${entity.contact_subtype ? ' - ' + entity.contact_subtype : ''})`;
                      }
                    }
                    
                    return (
                      <TableRow key={entity.campaign_entity_id}>
                        <TableCell className="font-medium">{entity.entity_name}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{entity.entity_class}</Badge>
                        </TableCell>
                        <TableCell className="text-sm">
                          {contactDisplay}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {entity.notes || '-'}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleEditEntity(entity)}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleRemoveEntity(entity.campaign_entity_id)}
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Recipient Dialog */}
      <Dialog open={addEntityDialogOpen} onOpenChange={setAddEntityDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editingEntity ? 'Edit Recipient' : 'Add Recipient to Campaign'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Select Account/Contact *</Label>
              <Select
                value={entityForm.crm_entity_id}
                onValueChange={(value) => {
                  setEntityForm({...entityForm, crm_entity_id: value, contact_entry_id: ''});
                  fetchContactsForEntity(value);
                }}
                disabled={editingEntity !== null}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Choose entity..." />
                </SelectTrigger>
                <SelectContent>
                  {crmEntities.map((entity) => (
                    <SelectItem key={entity.id} value={entity.id}>
                      {entity.system_name} ({entity.crm_class})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {editingEntity && (
                <p className="text-xs text-muted-foreground">Entity cannot be changed when editing</p>
              )}
            </div>

            {entityForm.crm_entity_id && (
              <div className="space-y-2">
                <Label>Contact Detail to Use</Label>
                <Select
                  value={entityForm.contact_entry_id || 'none'}
                  onValueChange={(value) => setEntityForm({...entityForm, contact_entry_id: value === 'none' ? '' : value})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select contact detail" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Use any available</SelectItem>
                    {contactEntries
                      .filter(contact => {
                        // Filter based on campaign type
                        const campaignType = selectedCampaign?.campaign_type;
                        if (campaignType === 'Email') {
                          return contact.contact_type === 'email';
                        } else if (campaignType === 'Phone') {
                          return contact.contact_type === 'phone';
                        } else if (campaignType === 'Direct Mail') {
                          return contact.contact_type === 'address';
                        }
                        return true; // Show all for other types
                      }).length === 0 ? (
                        <SelectItem value="no-contacts" disabled>
                          No {selectedCampaign?.campaign_type === 'Email' ? 'email addresses' : 
                             selectedCampaign?.campaign_type === 'Phone' ? 'phone numbers' : 
                             selectedCampaign?.campaign_type === 'Direct Mail' ? 'addresses' : 'contacts'} on file
                        </SelectItem>
                      ) : (
                        contactEntries
                          .filter(contact => {
                            const campaignType = selectedCampaign?.campaign_type;
                            if (campaignType === 'Email') return contact.contact_type === 'email';
                            if (campaignType === 'Phone') return contact.contact_type === 'phone';
                            if (campaignType === 'Direct Mail') return contact.contact_type === 'address';
                            return true;
                          })
                          .map((contact) => (
                            <SelectItem key={contact.id} value={contact.id}>
                              {contact.contact_type === 'email' && `${contact.email_address} (Email${contact.email_type ? ' - ' + contact.email_type : ''})`}
                              {contact.contact_type === 'phone' && `${contact.phone_number} (Phone${contact.phone_type ? ' - ' + contact.phone_type : ''})`}
                              {contact.contact_type === 'address' && `${contact.street}, ${contact.city} (Address${contact.address_type ? ' - ' + contact.address_type : ''})`}
                            </SelectItem>
                          ))
                      )}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  {selectedCampaign?.campaign_type === 'Email' && 'Showing email contacts only'}
                  {selectedCampaign?.campaign_type === 'Phone' && 'Showing phone contacts only'}
                  {selectedCampaign?.campaign_type === 'Direct Mail' && 'Showing address contacts only'}
                  {!['Email', 'Phone', 'Direct Mail'].includes(selectedCampaign?.campaign_type) && 'Select specific contact or leave as "Use any available"'}
                </p>
              </div>
            )}

            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={entityForm.notes}
                onChange={(e) => setEntityForm({...entityForm, notes: e.target.value})}
                placeholder="Campaign-specific notes for this recipient..."
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddEntityDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveEntity} disabled={saving} className="bg-primary hover:bg-primary/90 text-white">
              {saving ? 'Saving...' : (editingEntity ? 'Update Recipient' : 'Add Recipient')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MarketingCampaignsTab;
